import { FC } from "react";
import styled from "styled-components";
import { Entry } from "contentful";

interface PerformanceMetric {
  id: string;
  fields: {
    label: string;
    minLabel: string;
    maxLabel: string;
    value: number; // Value between 1 and 5
  };
}

interface PerformanceRatingChartFields extends Entry {
  chartTitle: string;
  metrics: PerformanceMetric[];
}

interface PerformanceRatingChartProps {
  fields: PerformanceRatingChartFields;
}

const Container = styled.div`
  max-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: flex-between;
  gap: 16px;
`;

const Title = styled.h3`
  color: #191A1F;
  font-family: "Instrument Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 20px;
  margin: 0;
`;

const MetricWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricLabel = styled.span`
  color: #191A1F;
  font-family: "Instrument Sans";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.14px;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 2px;
`;

const Segment = styled.div<{ $filled: boolean }>`
  flex: 1;
  height: 10px;
  background: ${({ $filled }) => ($filled ? "#191A1F" : "#E5E5E5")};

  &:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }

  &:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

const RangeLabels = styled.div`
  display: flex;
  justify-content: space-between;

  span {
    color: #191A1F;
    font-family: "Instrument Sans";
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0.12px;
  }
`;

const PerformanceRatingChart: FC<PerformanceRatingChartProps> = ({ fields: { chartTitle, metrics } }) => {
  return (
    <Container>
      <Title>{chartTitle}</Title>
      {metrics.map((metric) => (
        <MetricWrapper key={metric.fields.label}>
          <MetricLabel>{metric.fields.label}</MetricLabel>
          <ProgressBarWrapper>
            {Array.from({ length: 5 }, (_, index) => (
              <Segment key={index} $filled={index < metric.fields.value} />
            ))}
          </ProgressBarWrapper>
          <RangeLabels>
            <span>{metric.fields.minLabel}</span>
            <span>{metric.fields.maxLabel}</span>
          </RangeLabels>
        </MetricWrapper>
      ))}
    </Container>
  );
};

export default PerformanceRatingChart;